import { Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { IObject } from '@type_definitions/datatypes';
import { IFileUpload, IFileUploadFailResponse, ResponseStatus } from './file-upload.service';

interface IFileUploadAddData extends JqueryFileUploadAddObject {
  url:string;
  formData:IObject;
}

@Component({
  selector: 'file-button',
  template: `
    <button [class]="buttonClass" [busyButton]="uploading" [style.pointer-events]="uploading ? 'none' : 'auto'" [disabled]="disabled">
      <input #fileupload type="file" name="file" [disabled]="disabled">
      <ng-content></ng-content>
    </button>
  `
})
export class FileButtonComponent implements OnInit, OnChanges {
  @Input() public url:string;
  @Input() public formData:IObject;
  @Input() public autoSelect:boolean;
  @Input() public autoUpload:boolean;
  @Input() public submitting:boolean;
  @Input() public uploading:boolean;
  @Input() public class?:string;
  @Input() public disabled?:boolean;
  @Input() public dropZone:string;

  @Output() public onFileAdd:EventEmitter<File> = new EventEmitter();
  @Output() public onAfterSubmit:EventEmitter<JqueryFileUploadEnhancedPromise<unknown>> = new EventEmitter();
  @Output() public onUploadStarted:EventEmitter<void> = new EventEmitter();
  @Output() public onUploadProgress:EventEmitter<number> = new EventEmitter();
  @Output() public onUploadDone:EventEmitter<IFileUpload> = new EventEmitter();
  @Output() public onUploadFailed:EventEmitter<IFileUploadFailResponse> = new EventEmitter();

  @ViewChild('fileupload', { static: true }) public elementRef:ElementRef;

  private fileUploadElement:JQuery<HTMLSelectElement>;
  private fileUploadData:IFileUploadAddData;
  public buttonClass:string;

  constructor() {
    /***/
  }

  public ngOnInit():void {
    this.fileUploadElement = $(this.elementRef.nativeElement);
    this.buttonClass = `btn ${this.class} fileinput-button`;

    const fileUploadOptions:JQueryFileInputOptions = {
      autoUpload: false,

      add: (_e:JQueryEventObject, data:IFileUploadAddData) => {
        this.fileUploadData = data;
        const file:File = data.files[ data.files.length - 1 ];
        this.onFileAdd.emit(file);
        if (this.autoUpload) {
          this.submit();
        }
      },

      progress: (_e:JQueryEventObject, data:JQueryFileUploadProgressObject) => {
        this.onUploadProgress.emit(Math.round(data.loaded / data.total * 100));
      },

      start: () => {
        this.onUploadStarted.emit();
      },

      done: (_e:JQueryEventObject, data:JQueryFileUploadDone) => {
        if (data?.result?.status === ResponseStatus.OK) {
          this.onUploadDone.emit({
            status: data.textStatus,
            response: data.result
          });
        } else {
          this.onUploadFailed.emit(data.jqXHR?.responseJSON); // failed by the REST API response
        }
      },

      fail: (_e:JQueryEventObject, data:JQueryFileUploadDone) => {
        this.onUploadFailed.emit(data.jqXHR?.responseJSON); // failed when detected by jQuery
      }
    };

    // whenever 'autoUpload' is defined then disabled drag n drop functionality
    if (this.autoUpload) {
      fileUploadOptions.dropZone = null;
    }

    // when 'dropZone' selector is defined then overwrite the previous setting
    if (this.dropZone) {
      fileUploadOptions.dropZone = $(this.dropZone);
    }

    this.fileUploadElement.fileupload(fileUploadOptions);

    if (this.autoSelect) {
      this.fileUploadElement.focus().trigger('click');
    }
  }

  public ngOnChanges(changes:SimpleChanges):void {
    if (changes?.submitting?.currentValue) {
      this.submit();
    }
  }

  private submit() {
    this.fileUploadData.url = this.url;
    this.fileUploadData.formData = this.formData;
    const submitHandler:JqueryFileUploadEnhancedPromise<XMLHttpRequest> = this.fileUploadData.submit();
    setTimeout(() => this.onAfterSubmit.emit(submitHandler));
  }
}